import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { 
  acceptAllCookies, 
  acceptSelectedCookies, 
  declineAllCookies,
  shouldShowConsentBanner 
} from '../../utils/cookieUtils';

const CookieConsent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: true,
    marketing: false
  });

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    if (shouldShowConsentBanner()) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  if (location.pathname.startsWith('/admin')) return null;

  const handleAcceptAll = () => {
    acceptAllCookies();
    setIsVisible(false);
    window.location.reload(); // Reload to initialize analytics
  };

  const handleAcceptSelected = () => {
    acceptSelectedCookies(preferences);
    setIsVisible(false);
    if (preferences.analytics) {
      window.location.reload();
    }
  };

  const handleDeclineAll = () => {
    declineAllCookies();
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-40 pointer-events-none"
        >
          <div className="max-w-md ml-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden pointer-events-auto">
            {/* Main Content */}
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    {t('cookies.title')}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {t('cookies.message')}
                  </p>
                </div>
                <button
                  onClick={handleDeclineAll}
                  className="sm:hidden p-1.5 text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Expandable Details */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mt-4 transition-colors"
              >
                {t('cookies.manage_preferences')}
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 space-y-3 pt-4 border-t border-slate-100">
                      {/* Essential Cookies */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{t('cookies.essential')}</p>
                          <p className="text-xs text-slate-400">{t('cookies.essential_desc')}</p>
                        </div>
                        <div className="w-10 h-6 bg-slate-900 rounded-full flex items-center justify-end px-1 opacity-50 cursor-not-allowed">
                          <div className="w-4 h-4 bg-white rounded-full" />
                        </div>
                      </div>

                      {/* Analytics Cookies */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{t('cookies.analytics')}</p>
                          <p className="text-xs text-slate-400">{t('cookies.analytics_desc')}</p>
                        </div>
                        <button
                          onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                          className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                            preferences.analytics ? 'bg-slate-900 justify-end' : 'bg-slate-200 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
                        </button>
                      </div>

                      {/* Marketing Cookies */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{t('cookies.marketing')}</p>
                          <p className="text-xs text-slate-400">{t('cookies.marketing_desc')}</p>
                        </div>
                        <button
                          onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                          className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                            preferences.marketing ? 'bg-slate-900 justify-end' : 'bg-slate-200 justify-start'
                          }`}
                        >
                          <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-5">
                <button
                  onClick={handleDeclineAll}
                  className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg transition-colors order-3 sm:order-1"
                >
                  {t('cookies.decline_all')}
                </button>
                {showDetails && (
                  <button
                    onClick={handleAcceptSelected}
                    className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors order-2"
                  >
                    {t('cookies.save_preferences')}
                  </button>
                )}
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors sm:ml-auto order-1 sm:order-3"
                >
                  {t('cookies.accept_all')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
